import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { ITableFiltersProps, IFilterModel, ISortModel, IColumnConfig } from '@/types/admin/dataTable';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { SearchBar } from './SearchBar';

export const TableFilters = <T,>({
  columns,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  headerRight,
  enableRowSelection = false,
  selectedRows = [],
  totalRows = 0,
  onSelectAll,
  onDeselectAll,
}: ITableFiltersProps<T>) => {
  const [showFilters, setShowFilters] = useState(false);

  // Get filterable columns
  const filterableColumns = columns.filter(col => col.filterable !== false);
  
  // Get sortable columns
  const sortableColumns = columns.filter(col => col.sortable !== false);

  // Handle filter toggle for select/multiSelect filters
  const handleFilterToggle = (field: string, value: any, isMultiSelect = false) => {
    const currentItems = filterModel.items.filter(item => item.field === field);
    const currentValues = currentItems.map(item => item.value);
    
    let newValues: any[];
    
    if (isMultiSelect) {
      if (currentValues.includes(value)) {
        newValues = currentValues.filter(v => v !== value);
      } else {
        newValues = [...currentValues, value];
      }
    } else {
      newValues = currentValues.includes(value) ? [] : [value];
    }

    // Update filter model
    const newFilterModel: IFilterModel = {
      items: [
        ...filterModel.items.filter(item => item.field !== field),
        ...newValues.map(val => ({
          field,
          operator: 'is' as const,
          value: val,
        })),
      ],
    };

    onFilterChange(newFilterModel);
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    const currentSort = sortModel[0];
    let newSort: ISortModel;

    if (currentSort?.field === field) {
      // Toggle sort direction
      newSort = {
        field,
        sort: currentSort.sort === 'asc' ? 'desc' : 'asc',
      };
    } else {
      // New field, default to ascending
      newSort = { field, sort: 'asc' };
    }

    onSortChange([newSort]);
  };

  const getSortIcon = (field: string): string => {
    const currentSort = sortModel[0];
    if (currentSort?.field === field) {
      return currentSort.sort === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  const getSelectedValues = (field: string): any[] => {
    return filterModel.items
      .filter(item => item.field === field)
      .map(item => item.value);
  };

  const renderFilterSection = (column: IColumnConfig<T>) => {
    const selectedValues = getSelectedValues(String(column.key));
    
    if (column.filterType === 'select' || column.filterType === 'multiSelect') {
      const isMultiSelect = column.filterType === 'multiSelect';
      
      return (
        <View key={String(column.key)} style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>{column.label}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.filterChipsContainer}>
              {column.filterOptions?.map((option) => (
                <TouchableOpacity
                  key={String(option.value)}
                  style={[
                    styles.filterChip,
                    selectedValues.includes(option.value) && styles.filterChipSelected,
                  ]}
                  onPress={() => handleFilterToggle(String(column.key), option.value, isMultiSelect)}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      selectedValues.includes(option.value) && styles.filterChipTextSelected,
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>
      );
    }
    
    // Add other filter types (text, date, etc.) as needed
    return null;
  };

  return (
    <View style={styles.container}>
      {/* Search Bar */}
      {showSearch && onSearchChange && (
        <SearchBar
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChangeText={onSearchChange}
          style={styles.searchBar}
        />
      )}

      {/* Filter Toggle */}
      <View style={styles.filterToggleRow}>
        <TouchableOpacity
          style={styles.filterToggle}
          onPress={() => setShowFilters(!showFilters)}
        >
          <View style={styles.filterToggleContent}>
            <Text style={styles.filterToggleText}>
              Filters
            </Text>
            <SVG_COMPONENTS.FILTER width={16} height={16} />
          </View>
        </TouchableOpacity>
        
        <View style={styles.rightButtons}>
          {headerRight && (
            <View style={styles.headerRightContainer}>
              {headerRight}
            </View>
          )}
          
          {/* Select All Button */}
          {enableRowSelection && totalRows > 0 && (
            <TouchableOpacity
              style={styles.selectAllToggle}
              onPress={() => {
                const allSelected = selectedRows.length === totalRows;
                if (allSelected) {
                  onDeselectAll?.();
                } else {
                  onSelectAll?.();
                }
              }}
            >
              <View style={styles.selectAllToggleContent}>
                <View style={[
                  styles.selectAllIndicator,
                  selectedRows.length === totalRows && styles.selectAllIndicatorSelected,
                  selectedRows.length > 0 && selectedRows.length < totalRows && styles.selectAllIndicatorIndeterminate
                ]}>
                  {selectedRows.length === totalRows && (
                    <SVG_COMPONENTS.CHECK_CIRCLE width={10} height={10} color="#FFFFFF" />
                  )}
                  {selectedRows.length > 0 && selectedRows.length < totalRows && (
                    <View style={styles.indeterminateBar} />
                  )}
                </View>
                <Text style={styles.selectAllText}>
                  {selectedRows.length === totalRows ? 'Deselect All' : 'Select All'}
                </Text>
                {selectedRows.length > 0 && (
                  <Text style={styles.selectionCount}>({selectedRows.length})</Text>
                )}
              </View>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Filters Panel */}
      {showFilters && (
        <View style={styles.filtersPanel}>
          {/* Dynamic Filter Sections */}
          {filterableColumns.map(column => renderFilterSection(column))}

          {/* Sort Options */}
          {sortableColumns.length > 0 && (
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterChipsContainer}>
                  {sortableColumns.map((column) => (
                    <TouchableOpacity
                      key={String(column.key)}
                      style={[
                        styles.sortChip,
                        sortModel[0]?.field === String(column.key) && styles.sortChipSelected,
                      ]}
                      onPress={() => handleSortChange(String(column.key))}
                    >
                      <Text
                        style={[
                          styles.sortChipText,
                          sortModel[0]?.field === String(column.key) && styles.sortChipTextSelected,
                        ]}
                      >
                        {column.label} {getSortIcon(String(column.key))}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.HEADER_BACKGROUND,
    borderBottomWidth: 2,
    borderBottomColor: ADMIN_COLORS.DATA_TABLE.BORDER,
    width: '100%',
    zIndex: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  searchBar: {
    marginHorizontal: 16,
    marginVertical: 12,
  },
  filterToggle: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    // borderTopWidth: 1,
    // borderTopColor: ADMIN_COLORS.DATA_TABLE.BORDER,
  },
  filterToggleRow: {
    paddingHorizontal: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rightButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  filterToggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterToggleText: {
    fontSize: 14,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
    fontWeight: 'bold',
  },
  headerRightContainer: {
    paddingRight: 16,
    paddingBottom: 8,
  },
  filtersPanel: {
    paddingHorizontal: 16,
    paddingTop: 10,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
  filterSection: {
    marginBottom: 16,
  },
  filterSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#757575',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  filterChipsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#BDBDBD',
    backgroundColor: '#FFFFFF',
  },
  filterChipSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  filterChipText: {
    fontSize: 12,
    color: '#212121',
  },
  filterChipTextSelected: {
    color: '#FFFFFF',
  },
  sortChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    backgroundColor: '#FFFFFF',
  },
  sortChipSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  sortChipText: {
    fontSize: 12,
    color: '#212121',
  },
  sortChipTextSelected: {
    color: '#FFFFFF',
  },
  // Select All styles
  selectAllToggle: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  selectAllToggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  selectAllIndicator: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectAllIndicatorSelected: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  selectAllIndicatorIndeterminate: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  indeterminateBar: {
    width: 6,
    height: 1.5,
    backgroundColor: '#FFFFFF',
  },
  selectAllText: {
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  selectionCount: {
    fontSize: 11,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
});
